import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Progress } from '../components/ui/progress';
import {
  Clock,
  Warning,
  CheckCircle,
  XCircle,
  Pill,
  FirstAid,
  User,
  Users,
  CaretDown,
  CaretUp,
  FunnelSimple,
  ChartBar,
  ListBullets,
  Timer,
  Target,
  TrendUp,
  TrendDown,
  Heartbeat,
  Stethoscope,
  Info
} from '@phosphor-icons/react';

// Colores por severidad
const SEVERITY_COLORS = {
  correcto: { bg: 'bg-green-500/20', border: 'border-green-500', text: 'text-green-400', dot: 'bg-green-500' },
  leve: { bg: 'bg-yellow-500/20', border: 'border-yellow-500', text: 'text-yellow-400', dot: 'bg-yellow-500' },
  moderado: { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400', dot: 'bg-orange-500' },
  grave: { bg: 'bg-red-500/20', border: 'border-red-500', text: 'text-red-400', dot: 'bg-red-500' },
  critico: { bg: 'bg-red-900/40', border: 'border-red-700', text: 'text-red-300', dot: 'bg-red-700' },
};

// Colores por timing status
const TIMING_COLORS = {
  on_time: { bg: 'bg-green-500/20', border: 'border-green-500', text: 'text-green-400', dot: 'bg-green-500', label: 'A Tiempo' },
  late: { bg: 'bg-yellow-500/20', border: 'border-yellow-500', text: 'text-yellow-400', dot: 'bg-yellow-500', label: 'Tardío' },
  critical: { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400', dot: 'bg-orange-500', label: 'Crítico' },
  out_of_window: { bg: 'bg-red-500/20', border: 'border-red-500', text: 'text-red-400', dot: 'bg-red-500', label: 'Fuera de Ventana' },
};

const SEVERITY_LABELS = {
  correcto: 'Correcto',
  leve: 'Leve',
  moderado: 'Moderado',
  grave: 'Grave',
  critico: 'Crítico',
};

const ERROR_TYPE_LABELS = {
  fuera_protocolo: 'Fuera de Protocolo',
  rol_no_autorizado: 'Rol No Autorizado',
  omision_critica: 'Omisión Crítica',
  retraso_clinico: 'Retraso Clínico',
  error_medicamento: 'Error de Medicamento',
  error_dosis: 'Error de Dosis',
  via_incorrecta: 'Vía Incorrecta',
};

const ROLE_LABELS = {
  medico: 'Médico',
  enfermera: 'Enfermera',
  tens: 'TENS',
  paramedico: 'Paramédico',
};

const EVENT_ICONS = {
  accion_usuario: FirstAid,
  accion_equipo: Users,
  medicamento_administrado: Pill,
  medicamento_indicado: Pill,
  error_clinico: XCircle,
  cambio_vitales: Heartbeat,
  alerta: Warning,
};

// Componente de evento individual en el timeline
function TimelineEventCard({ event, isExpanded, onToggle }) {
  const colors = SEVERITY_COLORS[event.severity] || SEVERITY_COLORS.correcto;
  const Icon = EVENT_ICONS[event.event_type] || FirstAid;
  
  return (
    <div 
      className={`relative pl-8 pb-4 ${isExpanded ? 'mb-2' : ''}`}
      data-testid={`timeline-event-${event.id}`}
    >
      {/* Línea vertical del timeline */}
      <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-slate-700" />
      
      {/* Punto del evento */}
      <div className={`absolute left-0 top-1 w-6 h-6 rounded-full ${colors.dot} flex items-center justify-center z-10`}>
        <Icon size={14} className="text-white" weight="bold" />
      </div>
      
      {/* Contenido del evento */}
      <div 
        className={`rounded-lg border ${colors.border} ${colors.bg} p-3 cursor-pointer transition-all hover:shadow-md`}
        onClick={onToggle}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-slate-400">{event.elapsed_time_formatted}</span>
              <Badge className={`${colors.bg} ${colors.text} text-xs`}>
                {SEVERITY_LABELS[event.severity]}
              </Badge>
              {event.role && (
                <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">
                  {ROLE_LABELS[event.role] || event.role}
                </Badge>
              )}
            </div>
            <p className={`font-medium ${event.is_adverse_event ? 'text-red-300' : 'text-white'}`}>
              {event.action}
            </p>
          </div>
          <Button variant="ghost" size="sm" className="p-1 h-auto">
            {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
          </Button>
        </div>
        
        {/* Detalles expandidos */}
        {isExpanded && (
          <div className="mt-3 pt-3 border-t border-slate-700 space-y-2 text-sm">
            {event.is_adverse_event && event.adverse_event_type && (
              <div className="flex items-start gap-2">
                <Warning size={16} className="text-orange-400 mt-0.5 shrink-0" />
                <div>
                  <span className="text-slate-400">Tipo de desviación: </span>
                  <span className="text-orange-300">{ERROR_TYPE_LABELS[event.adverse_event_type] || event.adverse_event_type}</span>
                </div>
              </div>
            )}
            
            {event.feedback && (
              <div className="flex items-start gap-2">
                <Info size={16} className="text-blue-400 mt-0.5 shrink-0" />
                <div>
                  <span className="text-slate-400">Razón: </span>
                  <span className="text-slate-300">{event.feedback}</span>
                </div>
              </div>
            )}
            
            {event.clinical_consequence && (
              <div className="flex items-start gap-2">
                <Heartbeat size={16} className="text-red-400 mt-0.5 shrink-0" />
                <div>
                  <span className="text-slate-400">Consecuencia clínica: </span>
                  <span className="text-red-300">{event.clinical_consequence}</span>
                </div>
              </div>
            )}
            
            {event.recommendation && (
              <div className="flex items-start gap-2">
                <Target size={16} className="text-green-400 mt-0.5 shrink-0" />
                <div>
                  <span className="text-slate-400">Recomendación: </span>
                  <span className="text-green-300">{event.recommendation}</span>
                </div>
              </div>
            )}
            
            {event.vitals_impact && (
              <div className="flex items-start gap-2">
                <Stethoscope size={16} className="text-cyan-400 mt-0.5 shrink-0" />
                <div>
                  <span className="text-slate-400">Signos vitales: </span>
                  <span className="text-cyan-300">
                    FC: {event.vitals_impact.fc || '--'} | 
                    PA: {event.vitals_impact.pa_sistolica || '--'}/{event.vitals_impact.pa_diastolica || '--'} | 
                    SpO2: {event.vitals_impact.spo2 || '--'}%
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Panel de resumen docente
function SummaryPanel({ analysis }) {
  const performanceColors = {
    excelente: 'text-green-400',
    bueno: 'text-blue-400',
    regular: 'text-yellow-400',
    deficiente: 'text-orange-400',
    'crítico': 'text-red-400',
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {/* Puntaje General */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">Puntaje General</span>
            <ChartBar size={20} className="text-blue-400" />
          </div>
          <div className="flex items-end gap-2">
            <span className={`text-3xl font-bold ${performanceColors[analysis.performance_level]}`}>
              {analysis.overall_score}
            </span>
            <span className="text-slate-400 text-sm mb-1">/100</span>
          </div>
          <Badge className={`mt-2 ${performanceColors[analysis.performance_level]} bg-transparent border`}>
            {analysis.performance_level.charAt(0).toUpperCase() + analysis.performance_level.slice(1)}
          </Badge>
        </CardContent>
      </Card>

      {/* Total de Desviaciones */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">Desviaciones</span>
            <Warning size={20} className="text-orange-400" />
          </div>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-orange-400">{analysis.adverse_events}</span>
            <span className="text-slate-400 text-sm mb-1">de {analysis.total_events}</span>
          </div>
          <Progress 
            value={(analysis.correct_events / Math.max(analysis.total_events, 1)) * 100} 
            className="mt-2 h-2" 
          />
        </CardContent>
      </Card>

      {/* Primer Error */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">Primer Error</span>
            <Timer size={20} className="text-red-400" />
          </div>
          {analysis.first_error ? (
            <>
              <span className="text-xl font-bold text-red-400">
                {analysis.first_error.elapsed_time_formatted}
              </span>
              <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                {analysis.first_error.action}
              </p>
            </>
          ) : (
            <span className="text-xl font-bold text-green-400">Sin errores</span>
          )}
        </CardContent>
      </Card>

      {/* Error Más Grave */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">Error Más Grave</span>
            <XCircle size={20} className="text-red-400" />
          </div>
          {analysis.most_severe_error ? (
            <>
              <Badge className={`${SEVERITY_COLORS[analysis.most_severe_error.severity]?.bg} ${SEVERITY_COLORS[analysis.most_severe_error.severity]?.text}`}>
                {SEVERITY_LABELS[analysis.most_severe_error.severity]}
              </Badge>
              <p className="text-xs text-slate-400 mt-2 line-clamp-2">
                {analysis.most_severe_error.action}
              </p>
            </>
          ) : (
            <span className="text-xl font-bold text-green-400">Ninguno</span>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Componente principal
export default function TimelineViewer({ analysis }) {
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterErrorType, setFilterErrorType] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [showCorrectEvents, setShowCorrectEvents] = useState(true);
  const [activeTab, setActiveTab] = useState('timeline');

  // Filtrar eventos
  const filteredEvents = useMemo(() => {
    if (!analysis?.events) return [];
    
    return analysis.events.filter(event => {
      if (filterSeverity !== 'all' && event.severity !== filterSeverity) return false;
      if (filterErrorType !== 'all' && event.adverse_event_type !== filterErrorType) return false;
      if (filterRole !== 'all' && event.role !== filterRole) return false;
      if (!showCorrectEvents && event.severity === 'correcto') return false;
      return true;
    });
  }, [analysis, filterSeverity, filterErrorType, filterRole, showCorrectEvents]);

  const toggleExpanded = (eventId) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedEvents(new Set(filteredEvents.map(e => e.id)));
  };

  const collapseAll = () => {
    setExpandedEvents(new Set());
  };

  if (!analysis) {
    return (
      <div className="text-center py-12 text-slate-400">
        No hay datos de timeline disponibles
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timeline-viewer">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Clock size={24} className="text-blue-400" />
            Análisis de Timeline
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            {analysis.scenario_name} | Rol: {ROLE_LABELS[analysis.clinical_role] || analysis.clinical_role} | 
            Duración: {analysis.total_duration_formatted}
          </p>
        </div>
      </div>

      {/* Panel de Resumen */}
      <SummaryPanel analysis={analysis} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="timeline" className="data-[state=active]:bg-slate-700 gap-2">
            <ListBullets size={16} />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="omissions" className="data-[state=active]:bg-slate-700 gap-2">
            <Warning size={16} />
            Omisiones ({analysis.omissions?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="delays" className="data-[state=active]:bg-slate-700 gap-2">
            <Timer size={16} />
            Retrasos ({analysis.clinical_delays?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="correlations" className="data-[state=active]:bg-slate-700 gap-2">
            <TrendDown size={16} />
            Correlaciones ({analysis.error_deterioration_correlations?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Tab: Timeline */}
        <TabsContent value="timeline" className="mt-4">
          {/* Filtros */}
          <Card className="bg-slate-800 border-slate-700 mb-4">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <FunnelSimple size={16} className="text-slate-400" />
                  <span className="text-sm text-slate-400">Filtros:</span>
                </div>
                
                <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                  <SelectTrigger className="w-[150px] bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Severidad" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="all" className="text-white">Todas</SelectItem>
                    {Object.entries(SEVERITY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterErrorType} onValueChange={setFilterErrorType}>
                  <SelectTrigger className="w-[180px] bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Tipo de Error" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="all" className="text-white">Todos</SelectItem>
                    {Object.entries(ERROR_TYPE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-[150px] bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Rol" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="all" className="text-white">Todos</SelectItem>
                    {Object.entries(ROLE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant={showCorrectEvents ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowCorrectEvents(!showCorrectEvents)}
                  className="gap-2"
                >
                  <CheckCircle size={14} />
                  Eventos Correctos
                </Button>

                <div className="flex-1" />

                <Button variant="ghost" size="sm" onClick={expandAll} className="text-slate-400">
                  Expandir Todo
                </Button>
                <Button variant="ghost" size="sm" onClick={collapseAll} className="text-slate-400">
                  Colapsar Todo
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Lista de eventos */}
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-1">
              {filteredEvents.length > 0 ? (
                filteredEvents.map(event => (
                  <TimelineEventCard
                    key={event.id}
                    event={event}
                    isExpanded={expandedEvents.has(event.id)}
                    onToggle={() => toggleExpanded(event.id)}
                  />
                ))
              ) : (
                <div className="text-center py-12 text-slate-400">
                  No hay eventos que coincidan con los filtros
                </div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Tab: Omisiones */}
        <TabsContent value="omissions" className="mt-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Warning size={20} className="text-orange-400" />
                Omisiones Críticas Detectadas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analysis.omissions?.length > 0 ? (
                <div className="space-y-3">
                  {analysis.omissions.map((omission, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-lg border ${SEVERITY_COLORS[omission.severity]?.border} ${SEVERITY_COLORS[omission.severity]?.bg}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-white">{omission.action_name}</p>
                          <p className="text-sm text-slate-400 mt-1">
                            Debió realizarse dentro de {omission.expected_within_seconds}s
                          </p>
                        </div>
                        <Badge className={`${SEVERITY_COLORS[omission.severity]?.text} bg-transparent border`}>
                          {SEVERITY_LABELS[omission.severity]}
                        </Badge>
                      </div>
                      <p className="text-sm text-red-300 mt-2">{omission.clinical_impact}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="mx-auto text-green-400 mb-2" />
                  <p className="text-green-400">No se detectaron omisiones críticas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Retrasos */}
        <TabsContent value="delays" className="mt-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Timer size={20} className="text-yellow-400" />
                Retrasos Clínicos Detectados
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analysis.clinical_delays?.length > 0 ? (
                <div className="space-y-3">
                  {analysis.clinical_delays.map((delay, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-lg border ${SEVERITY_COLORS[delay.severity]?.border} ${SEVERITY_COLORS[delay.severity]?.bg}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-white">{delay.action_name}</p>
                          <p className="text-sm text-slate-400 mt-1">
                            Realizado a los {Math.floor(delay.performed_at_seconds / 60)}:{(delay.performed_at_seconds % 60).toString().padStart(2, '0')} 
                            (esperado antes de {delay.expected_within_seconds}s)
                          </p>
                        </div>
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500">
                          +{delay.delay_seconds}s
                        </Badge>
                      </div>
                      <p className="text-sm text-yellow-300 mt-2">{delay.clinical_impact}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="mx-auto text-green-400 mb-2" />
                  <p className="text-green-400">No se detectaron retrasos clínicos significativos</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Correlaciones */}
        <TabsContent value="correlations" className="mt-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendDown size={20} className="text-red-400" />
                Correlación Error - Deterioro Clínico
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analysis.error_deterioration_correlations?.length > 0 ? (
                <div className="space-y-3">
                  {analysis.error_deterioration_correlations.map((corr, idx) => (
                    <div 
                      key={idx} 
                      className="p-4 rounded-lg border border-red-700 bg-red-900/20"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <XCircle size={16} className="text-red-400" />
                        <span className="font-medium text-white">{corr.error_action}</span>
                        <span className="text-slate-400">→</span>
                        <TrendDown size={16} className="text-red-400" />
                        <span className="text-red-300">{corr.deterioration_detected}</span>
                      </div>
                      <p className="text-sm text-slate-400">
                        Tiempo hasta deterioro: {corr.time_to_deterioration}s
                      </p>
                      <p className="text-sm text-red-300 mt-1">{corr.clinical_significance}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="mx-auto text-green-400 mb-2" />
                  <p className="text-green-400">No se detectaron correlaciones directas entre errores y deterioro</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Distribución de Severidad */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">Distribución por Severidad</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {Object.entries(SEVERITY_LABELS).map(([key, label]) => {
              const count = analysis.severity_counts?.[key] || 0;
              const percentage = analysis.total_events > 0 
                ? Math.round((count / analysis.total_events) * 100) 
                : 0;
              const colors = SEVERITY_COLORS[key];
              
              return (
                <div key={key} className="flex-1 min-w-[120px]">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm ${colors.text}`}>{label}</span>
                    <span className="text-slate-400 text-sm">{count}</span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${colors.dot} transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
